import React from 'react';
import { FinancialValue } from '../shared/FinancialValue';
import { StateChip } from '../shared/StateChip';
import { HonestState } from '../workspace/primitives/HonestState';

export interface TotalDemandComponent {
  ledger?: string | null;
  label?: string | null;
  value?: number | null;
  basis?: string | null;
}

export interface ReconstructedIndemnity {
  property_rcv?: number | null;
  property_acv?: number | null;
  bi_loss?: number | null;
  gross_loss?: number | null;
  deductible?: number | null;
  net_claim?: number | null;
  bi_net?: number | null;
  // P.5 — the payable PROPERTY ledger (L_PROP): net_property_payment → payable_rcv →
  // rcv_total. This is the property term of total_demand (NOT gross property_rcv).
  l_prop?: number | null;
  l_prop_basis?: string | null;
  l_prop_ledger?: string | null;
  // Gap-3 — layered PROPERTY disclosure sub-lines (engine-computed, shown but NOT in
  // total_demand): ordinance-or-law, debris removal, and held-back recoverable
  // depreciation (ACV-until-proof). matching_cost is deferred to the matching-UI port.
  property_ordinance_law_cost?: number | null;
  property_debris_removal?: number | null;
  property_recoverable_depreciation_held_back?: number | null;
  // The per-ledger addends of total_demand — a labeled composition, never attested.
  total_demand_composition?: TotalDemandComponent[] | null;
  total_demand_attested?: boolean | null;
  total_demand?: number | null;
  provenance?: Record<string, { engine_id?: string; run_id?: string; computed_at?: string }>;
}

interface ReconstructedIndemnityCardProps {
  reconstructedIndemnity?: ReconstructedIndemnity | null;
  filedClaimAmount?: number | null;
  filedClaimComputedAt?: string | null;
}

// TZ-safe: the backend stores TZ-naive ISO timestamps ("2026-08-26T04:43:31.290000").
// Slice the date portion directly — never round-trip through Date/UTC, which would shift the
// day for a naive timestamp in a non-UTC viewer locale (Fable owner-flag). Fall back verbatim.
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(dateStr);
  return m ? m[1] : dateStr;
}

// Footer provenance keeps the run TIME (K3 P2) so two same-day engine runs stay distinct —
// still a pure string slice, never a TZ-shifting Date round-trip.
function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(dateStr);
  return m ? `${m[1]} ${m[2]}` : formatDate(dateStr);
}

// Derivation-annotation formatter (mono, no $) for the "= a + b" operand line only — every
// PRIMARY figure goes through FinancialValue; this is a formula caption per the design ruling.
function fmtOperand(n: number | null | undefined): string {
  if (n == null) return '';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// P.5 — the precedence basis behind the payable L_PROP figure, so the reader can see
// whether a coinsurance penalty / salvage credit reduced it below gross RCV.
function basisNote(basis: string | null | undefined): string {
  switch (basis) {
    case 'net_property_payment': return 'net payable';
    case 'payable_rcv': return 'coinsurance-adj.';
    case 'rcv_total': return 'RCV';
    default: return '';
  }
}

export const ReconstructedIndemnityCard: React.FC<ReconstructedIndemnityCardProps> = ({
  reconstructedIndemnity,
  filedClaimAmount,
  filedClaimComputedAt,
}) => {
  if (!reconstructedIndemnity) {
    return (
      <HonestState
        tier="no-input"
        scale="panel"
        label="Reconstructed indemnity not computed"
        body="No property/BI reconstruction is available for this engagement yet."
      />
    );
  }

  const {
    property_rcv,
    property_acv,
    bi_loss,
    gross_loss,
    deductible,
    net_claim,
    bi_net,
    l_prop,
    l_prop_basis,
    l_prop_ledger,
    property_ordinance_law_cost,
    property_debris_removal,
    property_recoverable_depreciation_held_back,
    total_demand_composition,
    total_demand,
    provenance,
  } = reconstructedIndemnity;

  const hasMismatch =
    bi_net != null && filedClaimAmount != null && Math.abs(bi_net - filedClaimAmount) > 0.01;
  const biLabel = net_claim != null ? 'BUSINESS INTERRUPTION · NET' : 'BUSINESS INTERRUPTION · GROSS';

  const netClaimProv = provenance?.net_claim;
  const biLossProv = provenance?.bi_loss;
  const isStale =
    !!netClaimProv?.computed_at &&
    !!biLossProv?.computed_at &&
    new Date(netClaimProv.computed_at) < new Date(biLossProv.computed_at);

  // Dedupe provenance to one token per engine run for the footer strip.
  const provenanceEntries = new Map<string, { engine_id: string; computed_at: string }>();
  if (provenance) {
    Object.values(provenance).forEach((entry) => {
      if (entry?.engine_id && entry?.computed_at && !provenanceEntries.has(entry.engine_id)) {
        provenanceEntries.set(entry.engine_id, { engine_id: entry.engine_id, computed_at: entry.computed_at });
      }
    });
  }

  const hasProvenance = (field: string): boolean => !!provenance?.[field]?.engine_id;

  // A supporting-ledger row: engine-ATTRIBUTED figure, never an arithmetic chain.
  const LedgerRow = ({ label, field, value }: { label: string; field: string; value: number | null | undefined }) => (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_170px] items-baseline px-5 h-11 border-t border-[var(--color-hairline)]">
      <span className="text-[13px] text-[var(--color-text-secondary)]">{label}</span>
      <span className="text-[11px] text-[var(--color-text-muted)] font-mono">{provenance?.[field]?.engine_id || ''}</span>
      <div className="text-right">
        {hasProvenance(field) ? (
          <FinancialValue value={value} size="md" />
        ) : (
          <span className="inline-flex items-center justify-end gap-2">
            <FinancialValue value={value} size="md" />
            <StateChip state="needs-human" text="unverified" size="sm" />
          </span>
        )}
      </div>
    </div>
  );

  // P.5 — the labeled composition of total_demand (per-ledger addends). Prefer the
  // backend array; derive from l_prop/bi_net for older responses (back-compat).
  const composition: TotalDemandComponent[] =
    total_demand_composition && total_demand_composition.length
      ? total_demand_composition
      : ([
          l_prop != null ? { ledger: l_prop_ledger || 'L_PROP', value: l_prop, basis: l_prop_basis } : null,
          bi_net != null ? { ledger: 'L_BI', value: bi_net } : null,
        ].filter(Boolean) as TotalDemandComponent[]);
  // Surface gross RCV as a supporting row only when a coinsurance penalty / salvage credit
  // pushed the payable below it — otherwise it just repeats the hero.
  const showGrossRcvRow = property_rcv != null && l_prop != null && Math.abs(property_rcv - l_prop) > 0.01;

  // Gap-3 — layered property coverages the engine computes but the L_PROP payable chain
  // excludes. Rendered as a SEPARATE "disclosed · not in total demand" section (not folded
  // into the derivation ledger), so no reader can mistake them for demand components.
  const hasLayeredDisclosure =
    (property_ordinance_law_cost != null && property_ordinance_law_cost > 0) ||
    (property_debris_removal != null && property_debris_removal > 0) ||
    (property_recoverable_depreciation_held_back != null && property_recoverable_depreciation_held_back > 0);

  const heroCols = l_prop == null ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="px-5 h-12 flex items-center justify-between border-b border-[var(--color-border)]">
        <span className="text-[11px] uppercase tracking-[0.08em] font-medium text-[var(--color-text-muted)]">
          Reconstructed Indemnity
        </span>
        {hasMismatch && <StateChip state="warning" text="Run mismatch — reconcile" />}
      </div>

      {hasMismatch && (
        <div className="px-5 py-2 text-[11px] text-[var(--color-text-secondary)] border-b border-[var(--color-hairline)]">
          Differs from the filed calculation (<FinancialValue value={filedClaimAmount} size="sm" />, computed{' '}
          {formatDate(filedClaimComputedAt)}) — separate engine runs; reconcile before attestation.
        </div>
      )}

      <div className={`grid ${heroCols} border-b border-[var(--color-border)]`}>
        {l_prop != null && (
          <div className="px-5 py-4">
            <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] mb-2">
              PROPERTY · {l_prop_ledger || 'L_PROP'}
            </div>
            <div className="text-right">
              {hasProvenance('l_prop') || hasProvenance('property_rcv') ? (
                <FinancialValue value={l_prop} size="xl" />
              ) : (
                <span className="inline-flex items-center justify-end gap-2">
                  <FinancialValue value={l_prop} size="xl" />
                  <StateChip state="needs-human" text="unverified" size="sm" />
                </span>
              )}
            </div>
            {basisNote(l_prop_basis) && (
              <div className="text-right text-[11px] text-[var(--color-text-muted)] font-mono mt-1">
                {basisNote(l_prop_basis)}
              </div>
            )}
          </div>
        )}

        <div className={`px-5 py-4 ${property_rcv != null ? 'border-l border-[var(--color-hairline)]' : ''}`}>
          <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] mb-2">{biLabel}</div>
          <div className="text-right">
            <FinancialValue value={bi_net} size="xl" />
          </div>
          {net_claim != null && (
            <div className="text-right text-[11px] text-[var(--color-text-muted)] font-mono mt-1">= net claim</div>
          )}
        </div>

        <div className="px-5 py-4 border-l border-[var(--color-hairline)] bg-[var(--color-bg-elevated)]">
          <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)] mb-2">TOTAL DEMAND</div>
          <div className="text-right">
            <FinancialValue value={total_demand} size="xl" />
          </div>
          {/* P.5 — a LABELED per-ledger composition (payable L_PROP + L_BI), never an
              attested blend. The property operand is the PAYABLE, not gross RCV. */}
          {composition.length >= 2 ? (
            <>
              <div className="text-right text-[11px] text-[var(--color-text-muted)] font-mono mt-1">
                = {composition.map((c) => fmtOperand(c.value)).join(' + ')}
              </div>
              <div className="text-right text-[10px] text-[var(--color-text-muted)] font-mono mt-0.5">
                {composition.map((c) => c.ledger || '—').join(' + ')} · composition, not attested
              </div>
            </>
          ) : composition.length === 1 ? (
            <div className="text-right text-[11px] text-[var(--color-text-muted)] font-mono mt-1">
              {composition[0].ledger || '—'} only
            </div>
          ) : null}
        </div>
      </div>

      <div className="border-t border-[var(--color-border)]">
        {showGrossRcvRow && <LedgerRow label="Property RCV · gross (pre-coinsurance/salvage)" field="property_rcv" value={property_rcv} />}
        {property_acv != null && <LedgerRow label="Property ACV" field="property_acv" value={property_acv} />}
        {bi_loss != null && <LedgerRow label="BI loss · gross" field="bi_loss" value={bi_loss} />}
        {gross_loss != null && <LedgerRow label="Gross loss · dual-method" field="gross_loss" value={gross_loss} />}
        {deductible != null && <LedgerRow label="Deductible" field="deductible" value={deductible} />}
        {net_claim != null && <LedgerRow label="Net claim" field="net_claim" value={net_claim} />}
      </div>

      {/* Gap-3 — layered property coverages the engine computes but the L_PROP payable chain
          excludes (ordinance/law, debris, held-back recoverable depreciation). Kept in their
          OWN section, explicitly labeled "not in total demand", so they read as disclosure —
          never as demand components. Rows gate on > 0; matching cost lands with the port. */}
      {hasLayeredDisclosure && (
        <div className="border-t border-[var(--color-border)]">
          <div className="px-5 h-9 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.08em] font-medium text-[var(--color-text-muted)]">
              Layered property coverages
            </span>
            <span className="text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] font-mono">
              disclosed · not in total demand
            </span>
          </div>
          {property_ordinance_law_cost != null && property_ordinance_law_cost > 0 && (
            <LedgerRow label="Property · ordinance or law" field="property_ordinance_law_cost" value={property_ordinance_law_cost} />
          )}
          {property_debris_removal != null && property_debris_removal > 0 && (
            <LedgerRow label="Property · debris removal (gross allowance)" field="property_debris_removal" value={property_debris_removal} />
          )}
          {property_recoverable_depreciation_held_back != null && property_recoverable_depreciation_held_back > 0 && (
            <LedgerRow label="Property · recoverable depreciation (held back — payable on proof)" field="property_recoverable_depreciation_held_back" value={property_recoverable_depreciation_held_back} />
          )}
        </div>
      )}

      {isStale && (
        <div className="px-5 py-3 border-t border-[var(--color-hairline)]">
          <StateChip
            state="needs-human"
            text="Net claim predates latest BI re-run — reconcile before attestation."
          />
        </div>
      )}

      {provenanceEntries.size > 0 && (
        <div className="border-t border-[var(--color-hairline)] px-5 py-3 flex gap-6 flex-wrap">
          {Array.from(provenanceEntries.values()).map((entry) => (
            <span key={entry.engine_id} className="text-[11px] text-[var(--color-text-muted)] font-mono">
              ✓ SRC · {entry.engine_id} · {formatDateTime(entry.computed_at)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReconstructedIndemnityCard;
